import '../styles/Session.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import cheerLogo from '../images/cheerlogo.png';
import { Athlete } from '../types/types';
import { checkSessionCodeAndLogoutIfChanged } from '../utils/sessionUtils';
import {
  Button,
  Card,
  Container,
  Typography,
} from './common';
import NewAthleteTabs from './NewAthleteTabs';

export default function Session() {
  const { sessionId, athleteId } = useParams<{ sessionId: string; athleteId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState<any>(null);
  const [coachName, setCoachName] = useState('');
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const db = getFirestore(firebase);

  useEffect(() => {
    // Check if we have a sessionId in localStorage (from JoinSession)
    const storedSessionId = localStorage.getItem('sessionId');
    const storedCoachName = localStorage.getItem('coachName');

    checkSessionCodeAndLogoutIfChanged(sessionId!, navigate);

    if (!storedSessionId || !storedCoachName) {
      // If not, check if we have a sessionId from the URL
      if (!sessionId) {
        setError('No session ID found. Please join a session first.');
        setLoading(false);
        return;
      }

      // If we have a sessionId from the URL but not in localStorage, redirect to join
      navigate(`/join?redirect=${sessionId}`);
      return;
    }

    // If we have a sessionId in localStorage but not in the URL, redirect to the correct URL
    if (sessionId !== storedSessionId) {
      navigate(`/session/${storedSessionId}`);
      return;
    }

    setCoachName(storedCoachName);

    // Fetch session data and athletes
    async function fetchData() {
      try {
        if (!storedSessionId) return;

        // Fetch session data
        const sessionDoc = await getDoc(doc(db, 'sessions', storedSessionId));

        if (!sessionDoc.exists()) {
          setError('Session not found');
          return;
        }

        setSessionData(sessionDoc.data());

        // Fetch athletes assigned to this coach
        const athletesRef = collection(db, `sessions/${storedSessionId}/athletes`);
        const q = query(
          athletesRef,
          where('createdBy', '==', storedCoachName),
          // orderBy('number', 'asc')
        );
        const querySnapshot = await getDocs(q);

        const athletesList: Athlete[] = [];
        querySnapshot.forEach(doc => {
          athletesList.push({
            id: doc.id,
            ...doc.data()
          } as Athlete);
        });

        setAthletes(athletesList);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load session data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [sessionId, navigate, db]);

  const handleLogout = () => {
    setLoggingOut(true);
    localStorage.removeItem('sessionId');
    localStorage.removeItem('coachName');
    navigate('/join');
  };

  if (loading) {
    return (
      <Container>
        <></>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">{error}</div>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/join')}
          >
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card className='session-content'>
        <div className="session-header">
          <Typography variant="h2">Dashboard</Typography>
        </div>
        <div className="logo-container">
          <img src={cheerLogo} alt="Gryphon Logo" className="cheer-logo" />
        </div>
        <Typography variant="body">Coach: {coachName}</Typography>
        <Typography variant="body">Code: {sessionData?.code}</Typography>

        <div className="session-actions">
          <Typography variant="h3">Actions</Typography>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/add-athlete`)}
          >
            Add Athlete
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/athletes`)}
          >
            Score Athletes
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/athletes-final-result`)}
          >
            Rank Athletes
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/flash-cards`)}
          >
            Flash Cards
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/floor-layout`)}
          >
            Floor Layout
          </Button>

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(`/session/${sessionId}/export`)}
          >
            Export Athlete Data
          </Button>
          {sessionData?.code === 'TRYOUT' && (
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate(`/session/${sessionId}/test-playground`)}
            >
              Test Playground
            </Button>
          )}
          <Button
            variant="secondary"
            fullWidth
            onClick={() => setShowLogoutDialog(true)}
          >
            Logout
          </Button>
        </div>
      </Card>

      {showLogoutDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <Typography variant="h3">Logout</Typography>
            <Typography variant="body">
              Are you sure? This will log you out.
            </Typography>
            <div className="dialog-actions">
              <Button
                variant="secondary"
                onClick={() => setShowLogoutDialog(false)}
                disabled={loggingOut}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                {loggingOut ? 'Logging out...' : 'Logout'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <NewAthleteTabs 
        athletes={athletes}
        sessionId={sessionId!}
        currentAthleteId={athleteId}
        showHomeButton={true}
        onAthleteSelect={(athlete) => navigate(`/session/${sessionId}/athlete-page/${athlete.id}`)}
      />
    </Container>
  );
} 